/**
 * @file Submission read-only / review UI components — DatasetOverviewFormFlow.tsx.
 */

import React, { useMemo } from "react";
import type { AnyObj } from "../readonlyFormDisplay";
import "../CollapsiblePanel.css";
import "../SubmissionStagesFormReadonly.css";
import { buildDatasetOverviewSections } from "./buildDatasetOverviewSections";
import DatasetOverviewFormSection from "./DatasetOverviewFormSection";
import DatasetOverviewToolbar from "./DatasetOverviewToolbar";
import { useDatasetOverviewSections } from "./useDatasetOverviewSections";
import type { DatasetOverviewSectionContext } from "./types";

function backgroundFlagTrue(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

/** Type definition — Dataset Overview Form Flow Props. */
export type DatasetOverviewFormFlowProps = {
  submission: AnyObj;
};

/**
 * Full Dataset Overview read-only flow: toolbar + collapsible sections.
 * Reusable from contributor Dataset page and admin submission detail.
 */
const DatasetOverviewFormFlow: React.FC<DatasetOverviewFormFlowProps> = ({
  submission,
}) => {
  const background = (submission.background ?? null) as AnyObj | null;

  const sectionContext: DatasetOverviewSectionContext = useMemo(
    () => ({
      submission,
      showA1: backgroundFlagTrue(background?.a1),
      showA2: backgroundFlagTrue(background?.a2),
      showA3: backgroundFlagTrue(background?.a3),
      showA1A3: backgroundFlagTrue(background?.a1_a3),
    }),
    [submission, background]
  );

  const sections = useMemo(() => buildDatasetOverviewSections(), []);
  const { isExpanded, setSectionExpanded, expandAll, collapseAll } =
    useDatasetOverviewSections();

  return (
    <div
      className="submission-flow submission-stages-readonly"
      aria-readonly="true"
    >
      <DatasetOverviewToolbar onExpandAll={expandAll} onCollapseAll={collapseAll} />

      {sections.map((section) => (
        <DatasetOverviewFormSection
          key={section.key}
          sectionKey={section.key}
          title={section.title}
          description={section.description}
          expanded={isExpanded(section.key)}
          onExpandedChange={(open) => setSectionExpanded(section.key, open)}
        >
          {section.renderBody(sectionContext)}
        </DatasetOverviewFormSection>
      ))}
    </div>
  );
};

/** React component — Dataset Overview Form Flow. */
export default DatasetOverviewFormFlow;
